import { queries } from "@fxid/openapi-client";
import { setAuthToken } from "@fxid/shared";
import { Loader, Stack } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import React, { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import * as Analytics from "../../../packages/fxid-openapi-client/src/Analytics";

/**
 * @description
 * Компонент для аутентификации пользователя с использованием временного токена.
 * Он извлекает токен из параметров URL, обменивает его на постоянный токен сессии,
 * сохраняет его и перенаправляет пользователя на целевую страницу или на страницу ошибки.
 */
export const LoginWithTempToken = () => {
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();

	// Получаем временный токен напрямую из параметров URL.
	// Это избавляет от необходимости использовать `useState` и `useEffect` для его сохранения.
	const tempToken = searchParams.get("token");

	// React Query v5 рекомендует использовать статусы isSuccess/isError вместо колбэков onSuccess/onError.
	// Вся логика побочных эффектов выносится в useEffect, которые зависят от этих статусов.
	const { data, isSuccess, isError, error, isPending } = useQuery({
		...queries.auth.authenticateFromTempToken({ TempToken: tempToken! }),
		// Запрос будет выполнен только при наличии временного токена в URL.
		enabled: !!tempToken,
		// Это одноразовый запрос, нет смысла делать его "протухшим" и запрашивать повторно.
		staleTime: Infinity,
		// В случае ошибки (например, невалидный токен) нет смысла повторять запрос много раз.
		retry: false
	});

	// Этот useEffect обрабатывает все сценарии: отсутствие токена, успех и ошибку запроса.
	// Логика централизована в одном месте для лучшей читаемости и предсказуемости.
	useEffect(() => {
		// Если запрос в процессе выполнения, ничего не делаем.
		if (isPending) {
			log.debug("authentication from temp token is pending");
			return;
		}

		// Если `isPending` в значении `false`, но токена нет, значит, он отсутствовал в URL.
		// Это наша точка входа для проверки наличия токена.
		if (!tempToken) {
			log.error("No temp token in query parameters");
			navigate("/error");
			return;
		}

		// Обработка ошибки запроса.
		if (isError) {
			log.error("Failed to authenticate with temp token", error);
			navigate("/error");
			return;
		}

		// Обработка успешного ответа.
		if (isSuccess) {
			// Добавляем проверку на случай, если API вернет успешный статус, но без данных или токена.
			if (!data?.Token) {
				log.error("Authentication query succeeded but returned no or invalid data");
				navigate("/error");
				return;
			}

			const { Token, TempTokenData } = data;
			setAuthToken(Token);

			const redirectUri = TempTokenData?.RedirectUri ?? "/";

			log.debug("authentication from temp token is success, redirecting");
			Analytics.site
				.login({ method: "game_temp_token" })
				.catch((err) => {
					log.error("Analytics login event failed", err);
				})
				.finally(() => {
					// Перенаправляем пользователя после всех операций.
					navigate(redirectUri);
				});
		}
	}, [isPending, isSuccess, isError, data, tempToken, navigate, error]);

	// Пока происходят асинхронные операции, показываем индикатор загрузки.
	return (
		<Stack align="center" mt="xl">
			<Loader />
		</Stack>
	);
};
